import AttachFileIcon from '@mui/icons-material/AttachFile';
import MinimizeIcon from '@mui/icons-material/Minimize';
import { Backdrop, Box, Button, Divider, IconButton, Input, Paper, Tooltip, Typography } from '@mui/material';
import Portal from '@mui/material/Portal';
import { useToggle } from 'ahooks';
import { isEmpty } from 'lodash-es';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import FileUploader from '~/components/FileUploader';
import { useOfflineFileUploader } from '~/components/FileUploader/hooks';
import ArrowsExpandIcon from '~/components/icons/ArrowsExpand';
import XIcon from '~/components/icons/X';
import LoadingButton from '~/components/LoadingButton';
import RichTextEditor from '~/components/RichTextEditor';
import { useSession } from '~/core/session/hooks';
import { blobToDataUrl } from '~/lib/file';
import { useForwardEmail, useReplyEmail, useSendEmail } from './api/email';
import { EmailContactSelect } from './EmailContactSelect';
import { EmailAttachmentRequest, EmailDetailsResponse } from './models/email';

export interface MailComposerProps {
  defaultSubject?: string;
  defaultEmail?: string;
  open?: boolean;
  onClose?: () => void;
  onSent?: () => void;
  contacts?: { name: string; email: string }[];
  templates?: { name: string; body: string }[];
  reply?: EmailDetailsResponse;
  forward?: EmailDetailsResponse;
  mode?: 'floating' | 'inline';
}

const processInlineImages = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = doc.getElementsByTagName('img');
  const extractedData: Record<string, { contentType: string; content: string }> = {};

  Array.from(images).forEach((img) => {
    const src = img.getAttribute('src');
    if (src && src.startsWith('data:')) {
      const commaIndex = src.indexOf(',');
      if (commaIndex === -1) return; // 无效的Data URL

      const header = src.substring(0, commaIndex);
      const content = src.substring(commaIndex + 1);
      const contentType = header.split(';')[0].slice(5);
      const id = nanoid();

      extractedData[id] = {
        contentType: contentType,
        content: content
      };

      img.setAttribute('src', `cid:${id}`);
    }
  });

  return {
    html: doc.body.innerHTML,
    data: extractedData
  };
};

export const MailComposer: React.FC<MailComposerProps> = ({
  onClose,
  onSent,
  open,
  contacts,
  templates,
  reply,
  forward,
  defaultSubject = '',
  defaultEmail = '',
  mode = 'floating'
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [body, setBody] = useState(!isEmpty(templates) && templates?.[0]?.body ? templates?.[0]?.body : '');
  const [toAddresses, setToAddresses] = useState(defaultEmail);
  // const [ccAddresses, setCcAddresses] = useState('');
  // const [bccAddresses, setBccAddresses] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const { mutateAsync: sendEmail, isLoading: sending } = useSendEmail();
  const { mutateAsync: replyEmail, isLoading: replying } = useReplyEmail();
  const { mutateAsync: forwardEmail, isLoading: forwarding } = useForwardEmail();
  const canSend = body && toAddresses && subject;
  const { user } = useSession();
  const [showingAttachmentPanel, { toggle: toggleAttachmentPanel }] = useToggle();
  const attachmentUploader = useOfflineFileUploader();

  useEffect(() => {
    if (reply) {
      setToAddresses(
        reply.from.address === user?.email ? reply.toRecipients.map((it) => it.address).join(', ') : reply.from.address
      );
      setSubject(`Re: ${reply.subject}`);
    }
    if (forward) {
      setSubject(`Fwd: ${forward.subject}`);
    }
  }, [reply, forward]);

  const clear = () => {
    setToAddresses('');
    setSubject('');
    setBody('');
    attachmentUploader.clear();
  };

  const handleSelectContact = (contact: { name: string; email: string }) => {
    const currentToAddresses = toAddresses
      .split(',')
      .map((it) => it.trim())
      .filter(Boolean);
    if (currentToAddresses.includes(contact.email)) {
      return;
    }
    setToAddresses(currentToAddresses.concat(contact.email).join(', '));
  };

  const handleSendEmail = async () => {
    const { html: emailBody, data: inlineImages } = processInlineImages(body);
    const inlineAttachments = Object.entries(inlineImages).map<EmailAttachmentRequest>(
      ([cid, { contentType, content }]) => ({
        name: cid,
        location: '',
        size: content.length,
        contentType,
        signedUrl: '',
        content,
        inline: true,
        contentId: cid
      })
    );
    const attachments = await Promise.all(
      attachmentUploader.uploadFileStatus.map<Promise<EmailAttachmentRequest>>(async (it) => ({
        name: it.filename,
        location: '',
        size: it.filesize,
        contentType: it.filetype,
        signedUrl: '',
        content: (await blobToDataUrl(it.file!)).split(',')[1],
        inline: false,
        contentId: it.id
      }))
    );
    if (reply) {
      await replyEmail({
        emailId: reply.id,
        fromAddress: user?.email,
        toAddresses: toAddresses.split(',').map((it) => it.trim()),
        body: emailBody,
        attachments: [...inlineAttachments, ...attachments]
      });
      toast.success('Successfully replied!');
    } else if (forward) {
      await forwardEmail({
        emailId: forward.id,
        fromAddress: user?.email,
        toAddresses: toAddresses.split(',').map((it) => it.trim()),
        body: emailBody
      });
      toast.success('Successfully forwarded!');
    } else {
      await sendEmail({
        fromAddress: user?.email,
        toAddresses: toAddresses.split(',').map((it) => it.trim()),
        // ccAddresses: ccAddresses.split(',').map((it) => it.trim()),
        // bccAddresses: bccAddresses.split(',').map((it) => it.trim()),
        subject,
        body: emailBody,
        attachments: [...inlineAttachments, ...attachments]
      });
      toast.success('Successfully sent!');
    }

    onClose?.();
    onSent?.();
    clear();
  };

  const handleChange = (value: string) => {
    setBody(value);
  };

  const handleExitFullScreen = () => {
    setIsFullScreen(false);
  };

  const handleEnterFullScreen = () => {
    setIsFullScreen(true);
  };

  if (!open && mode === 'floating') {
    return null;
  }

  const inlineStyles = {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%'
  };

  const floatingStyles = {
    bottom: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    margin: 3,
    maxHeight: (theme: any) => `calc(100% - ${theme.spacing(6)})`,
    maxWidth: (theme: any) => `calc(100% - ${theme.spacing(6)})`,
    outline: 'none',
    position: 'fixed' as const,
    right: 0,
    width: 600,
    zIndex: 1300,
    overflow: 'hidden',
    ...(isFullScreen && {
      borderRadius: 0,
      height: '100%',
      margin: 0,
      maxHeight: '100%',
      maxWidth: '100%',
      width: '100%'
    })
  };

  const paperContent = (
    <Paper sx={mode === 'inline' ? inlineStyles : floatingStyles} elevation={mode === 'inline' ? 0 : 12}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          px: 2,
          py: 1
        }}
      >
        <Typography variant="h6">{reply ? 'Reply' : forward ? 'Forward Message' : 'New Message'}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        {mode === 'floating' && (
          <>
            {isFullScreen ? (
              <IconButton onClick={handleExitFullScreen}>
                <MinimizeIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton onClick={handleEnterFullScreen}>
                <ArrowsExpandIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
        <IconButton onClick={onClose}>
          <XIcon fontSize="small" />
        </IconButton>
      </Box>

      {!isEmpty(templates) && templates!.length > 1 && (
        <Box sx={{ p: 1 }}>
          {templates?.map((it) => (
            <Button key={it.name} variant="outlined" onClick={() => setBody(it.body)}>
              {it.name}
            </Button>
          ))}
        </Box>
      )}

      <Input
        disableUnderline
        fullWidth
        placeholder="To"
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
        value={toAddresses}
        onChange={(e) => setToAddresses(e.target.value)}
        endAdornment={<EmailContactSelect contacts={contacts} onSelect={handleSelectContact} />}
      />
      {/* <Input
          disableUnderline
          fullWidth
          placeholder="Cc"
          sx={{
            p: 1,
            borderBottom: 1,
            borderColor: 'divider'
          }}
          value={ccAddresses}
          onChange={(e) => setCcAddresses(e.target.value)}
        />
        <Input
          disableUnderline
          fullWidth
          placeholder="Bcc"
          sx={{
            p: 1,
            borderBottom: 1,
            borderColor: 'divider'
          }}
          value={bccAddresses}
          onChange={(e) => setBccAddresses(e.target.value)}
        /> */}
      <Input
        disableUnderline
        fullWidth
        placeholder="Subject"
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <RichTextEditor border={false} minHeight={250} onChange={handleChange} value={body} />
      {showingAttachmentPanel && (
        <>
          <Divider />
          <FileUploader
            uploadFileStatus={attachmentUploader.uploadFileStatus}
            onUpload={(file) => attachmentUploader.upload(Math.random().toString(), file)}
            onRemove={attachmentUploader.remove}
          />
        </>
      )}
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'flex-end',
          p: 2
        }}
      >
        <LoadingButton
          disabled={!canSend}
          loading={sending || replying || forwarding}
          variant="contained"
          onClick={handleSendEmail}
        >
          {reply ? 'Reply' : forward ? 'Forward' : 'Send'}
        </LoadingButton>

        <Tooltip title="Attach file">
          <IconButton size="small" sx={{ ml: 1 }} onClick={toggleAttachmentPanel}>
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );

  if (mode === 'floating') {
    return (
      <Portal>
        {isFullScreen && <Backdrop open={isFullScreen} />}
        {paperContent}
      </Portal>
    );
  }

  return paperContent;
};
